// 原生登入 — 平台分流（Pat 決策 2026-07-07）
//   iOS：只用 Sign in with Apple（訂閱走 Apple IAP）
//   Android：只用 Google Sign-In（訂閱走 Google Play Billing）
//   Web（PWA）：email 密碼 / Magic Link（pos-login 自理）
// see architecture.md#L280
//
// Apple 用 App 內自製 plugin（ios/App/App/AppleSignInPlugin.swift，見 signInWithApple 註解）；
// Google 用 @capgo/capacitor-social-login。兩者都 → supabase.auth.signInWithIdToken。

import { getSupabase } from './supabase-client.js';

/** 目前 Capacitor 平台：'ios' | 'android' | 'web' */
export function platform() {
  return window.Capacitor?.getPlatform?.() || 'web';
}
export function isNativeApp() {
  return !!(window.Capacitor?.isNativePlatform?.());
}

/** SHA-256 hex（nonce：raw 傳 Supabase、hashed 傳 Apple） */
async function sha256Hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
function randomNonce() {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** iOS：Sign in with Apple → Supabase session
 *  用 App 內自製原生 plugin（ios/App/App/AppleSignInPlugin.swift）：
 *  - 帶 presentationContextProvider（第三方 plugin 缺這個）
 *  - 失敗時回完整 NSError（domain/code/underlying）— 可診斷，不再只有 1001 一句話 */
export async function signInWithApple() {
  const { registerPlugin } = await import('@capacitor/core');
  const AppleSignIn = registerPlugin('AppleSignIn');
  const rawNonce = randomNonce();
  const hashedNonce = await sha256Hex(rawNonce);
  const res = await AppleSignIn.authorize({
    scopes: 'email name',
    nonce: hashedNonce,
  });
  const idToken = res?.identityToken;
  if (!idToken) throw new Error('Apple 未回傳 identity token');
  const { error } = await getSupabase().auth.signInWithIdToken({
    provider: 'apple',
    token: idToken,
    nonce: rawNonce,              // Supabase 驗 sha256(rawNonce) === idToken.nonce
  });
  if (error) throw error;
  // Apple 5.1.1(v)：刪帳號時要撤銷 Apple token，而撤銷需要 refresh token。
  // signInWithIdToken 只吃 identityToken，所以把一次性的 authorizationCode 交給後端換取。
  // fire-and-forget：這步失敗不能擋登入。見 netlify/functions/apple-link.js
  if (res?.authorizationCode) {
    linkAppleToken(res.authorizationCode).catch(() => {});
  }
}

/** 把 authorization code 交給後端換 refresh token（合規用，失敗不影響登入） */
async function linkAppleToken(authorizationCode) {
  const { apiUrl } = await import('./api-base.js');
  const { data } = await getSupabase().auth.getSession();
  const token = data?.session?.access_token;
  if (!token) return;
  // client_id 必須是發出這張 code 的 bundle ID，且要在後端白名單內
  const clientId = import.meta.env.VITE_IOS_BUNDLE_ID || 'app.siriusnexus.taukepos';
  await fetch(apiUrl('/.netlify/functions/apple-link'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ authorization_code: authorizationCode, client_id: clientId }),
  });
}

/** Android：Google Sign-In → Supabase session */
export async function signInWithGoogle() {
  const { SocialLogin } = await import('@capgo/capacitor-social-login');
  const googleWebClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || '';
  await SocialLogin.initialize({
    google: googleWebClientId ? { webClientId: googleWebClientId } : {},
  });
  const res = await SocialLogin.login({
    provider: 'google',
    options: { scopes: ['email', 'profile'] },
  });
  const idToken = res?.result?.idToken;
  if (!idToken) throw new Error('Google 未回傳 idToken');
  const { error } = await getSupabase().auth.signInWithIdToken({
    provider: 'google',
    token: idToken,
  });
  if (error) throw error;
}
